import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-toastify';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Card, CardHeader, Button, Input, Select, Textarea, Spinner } from '../components/ui';
import { useOrderStore, useMedicationStore, usePatientStore } from '../stores';
import { orderSchema, type OrderFormData } from '../utils/validation';

export const OrderForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const { createOrder, getOrderById, selectedOrder, isLoading: orderLoading } = useOrderStore();
  const { medications, fetchMedications, isLoading: medLoading } = useMedicationStore();
  const { patients, fetchPatients, isLoading: patientLoading } = usePatientStore();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<OrderFormData>({
    resolver: zodResolver(orderSchema),
    defaultValues: {
      patientId: '',
      medicationId: '',
      quantity: 1,
      notes: '',
    },
  });

  useEffect(() => {
    fetchMedications();
    fetchPatients();

    if (isEdit && id) {
      getOrderById(id);
    }
  }, [fetchMedications, fetchPatients, getOrderById, id, isEdit]);

  useEffect(() => {
    if (isEdit && selectedOrder) {
      reset({
        patientId: selectedOrder.patientId,
        medicationId: selectedOrder.medicationId,
        quantity: selectedOrder.quantity,
        notes: selectedOrder.notes || '',
      });
    }
  }, [isEdit, selectedOrder, reset]);

  const onSubmit = async (data: OrderFormData) => {
    setIsSubmitting(true);
    try {
      await createOrder(data);
      toast.success(isEdit ? 'Order updated successfully' : 'Order created successfully');
      navigate('/orders');
    } catch (error) {
      toast.error(isEdit ? 'Failed to update order' : 'Failed to create order');
    } finally {
      setIsSubmitting(false);
    }
  };

  const patientOptions = patients.map((p) => ({
    value: p.id,
    label: `${p.firstName} ${p.lastName}`,
  }));

  const medicationOptions = medications.map((m) => ({
    value: m.id,
    label: `${m.name} - ${m.dosage}`,
  }));

  if (orderLoading || medLoading || patientLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/orders')}
          className="p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 text-secondary-500 dark:text-secondary-400" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
            {isEdit ? 'Edit Order' : 'New Order'}
          </h1>
          <p className="text-secondary-500 dark:text-secondary-400">
            {isEdit ? 'Update order details' : 'Create a new patient order'}
          </p>
        </div>
      </div>

      {/* Form */}
      <Card>
        <CardHeader title="Order Details" subtitle="Fill in the order information" />

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <Controller
            name="patientId"
            control={control}
            render={({ field }) => (
              <Select
                label="Patient"
                placeholder="Select a patient"
                options={patientOptions}
                error={errors.patientId?.message}
                {...field}
              />
            )}
          />

          <Controller
            name="medicationId"
            control={control}
            render={({ field }) => (
              <Select
                label="Medication"
                placeholder="Select a medication"
                options={medicationOptions}
                error={errors.medicationId?.message}
                {...field}
              />
            )}
          />

          <Input
            label="Quantity"
            type="number"
            min={1}
            error={errors.quantity?.message}
            {...register('quantity', { valueAsNumber: true })}
          />

          <Textarea
            label="Notes"
            placeholder="Additional notes or instructions..."
            rows={4}
            error={errors.notes?.message}
            {...register('notes')}
          />

          <div className="flex justify-end space-x-3 pt-4 border-t border-secondary-100 dark:border-secondary-700">
            <Button type="button" variant="secondary" onClick={() => navigate('/orders')}>
              Cancel
            </Button>
            <Button type="submit" isLoading={isSubmitting}>
              {isEdit ? 'Update Order' : 'Create Order'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default OrderForm;



